import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Package, PackageSearch, Truck } from "lucide-react";
import { inr } from "@/lib/api";
import Particles, { GradientMesh } from "@/components/Particles";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const { state } = useLocation();
  const order = state?.order;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-28" data-testid="order-success-page">
      <GradientMesh />
      <Particles count={16} />
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="glass-card relative w-full max-w-lg rounded-[2rem] p-8 text-center sm:p-12"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.25, type: "spring", stiffness: 240, damping: 14 }}
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-full glow-mint"
          style={{ background: "var(--mn-surface)" }}
        >
          <CheckCircle2 size={38} style={{ color: "var(--mn-mint)" }} />
        </motion.div>
        <h1 className="mt-6 font-serif text-3xl tracking-tight">
          Your ritual is <span className="gold-text italic">on its way.</span>
        </h1>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--mn-text2)" }}>
          {order?.payment_method === "cod"
            ? "Order placed — pay when it arrives at your door."
            : "Payment confirmed. Your formulas are being wrapped in recycled silk paper."}
        </p>
        <div className="mt-6 rounded-2xl border p-4" style={{ borderColor: "var(--mn-glass-border)", background: "var(--mn-surface)" }}>
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-text2)" }}>Order ID</p>
          <p className="mt-1 font-mono text-xl" style={{ color: "var(--mn-gold)" }} data-testid="order-id">{orderId}</p>
          {order && (
            <p className="mt-1 font-mono text-xs" style={{ color: "var(--mn-text2)" }}>
              {order.items.length} item(s) · {inr(order.total)} · {order.payment_method.toUpperCase()}
            </p>
          )}
        </div>
        <div className="mt-6 flex items-center justify-center gap-6 font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--mn-text2)" }}>
          <span className="flex items-center gap-1.5"><Package size={13} /> Ships in 24h</span>
          <span className="flex items-center gap-1.5"><Truck size={13} /> 3–5 day delivery</span>
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to={`/track?order=${orderId}`} data-testid="track-order-link" className="btn-outline-glow inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm">
            <PackageSearch size={15} style={{ color: "var(--mn-mint)" }} /> Track This Order
          </Link>
          <Link to="/shop" data-testid="continue-shopping" className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm">
            Continue the Ritual <ArrowRight size={15} />
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
